import { useCallback, useEffect, useRef, useState } from 'react';
import CommunitySearch from '@/components/community/CommunitySearch';
import SponsorCard from '@/components/community/SponsorCard';
import ClassroomCard from '@/components/community/ClassroomCard';
import CommunityPostCard from '@/components/community/CommunityPostCard';
import LockerRoomCard from '@/components/community/LockerRoomCard';
import type { CommunityPost } from '@/components/community/CommunityPostCard';
import { getCommunityHighlights, getCommunitySponsors, getCommunityClassrooms, getCommunityLockerRooms, isApiError } from '@/lib/api';
import type { CommunitySponsorSummary, CommunityClassroomSummary, CommunityLockerRoomSummary } from '@/types/community';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

const FALLBACK_ERROR = "Couldn't load content. Try again.";
const EMPTY_SPONSORS_MESSAGE = 'No sponsors yet.';
const EMPTY_CLASSROOMS_MESSAGE = 'No classrooms available yet.';
const ROTATION_INTERVAL_MS = 12000; // 12 seconds between rotations

export default function CommunityLanding() {
  const [searchResults, setSearchResults] = useState<CommunitySponsorSummary[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchState, setSearchState] = useState<{ loading: boolean; error: string | null; notFound: boolean }>({
    loading: false,
    error: null,
    notFound: false,
  });

  const [featured, setFeatured] = useState<CommunitySponsorSummary[]>([]);
  const [featuredError, setFeaturedError] = useState<string | null>(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredMessage, setFeaturedMessage] = useState<string | null>(null);

  const [communityPicks, setCommunityPicks] = useState<CommunitySponsorSummary[]>([]);
  const [communityState, setCommunityState] = useState<{ loading: boolean; error: string | null; message: string | null }>({
    loading: false,
    error: null,
    message: null,
  });

  // Classroom rotation state
  const [classrooms, setClassrooms] = useState<CommunityClassroomSummary[]>([]);
  const [classroomsState, setClassroomsState] = useState<{ loading: boolean; error: string | null; message: string | null }>({
    loading: true,
    error: null,
    message: null,
  });

  // Locker room state
  const [lockerRooms, setLockerRooms] = useState<CommunityLockerRoomSummary[]>([]);
  const [lockerRoomsState, setLockerRoomsState] = useState<{ loading: boolean; error: string | null; message: string | null }>({
    loading: true,
    error: null,
    message: null,
  });

  // Shared club posts
  const [clubPosts, setClubPosts] = useState<CommunityPost[]>([]);
  const [clubPostsLoading, setClubPostsLoading] = useState(true);

  // Auto-rotation state for community sponsors
  const [isRotationPaused, setIsRotationPaused] = useState(false);
  const [rotationProgress, setRotationProgress] = useState(0);
  const rotationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSearchResults = useCallback((results: CommunitySponsorSummary[], query: string) => {
    setSearchResults(results);
    setSearchQuery(query);
  }, []);

  const handleSearchState = useCallback((state: { query: string; loading: boolean; error: string | null; notFound: boolean }) => {
    setSearchState({ loading: state.loading, error: state.error, notFound: state.notFound });
  }, []);

  const loadFeatured = useCallback(async () => {
    setFeaturedLoading(true);
    setFeaturedError(null);
    setFeaturedMessage(null);
    try {
      const items = await getCommunitySponsors('', { limit: 12 });
      setFeatured(items);
      if (!items.length) {
        setFeaturedMessage(EMPTY_SPONSORS_MESSAGE);
      }
    } catch (err) {
      console.error('community featured error', err);
      if (isApiError(err) && err.status === 404) {
        setFeatured([]);
        setFeaturedMessage(EMPTY_SPONSORS_MESSAGE);
      } else {
        setFeaturedError(FALLBACK_ERROR);
        setFeatured([]);
      }
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  const loadCommunity = useCallback(async () => {
    setCommunityState({ loading: true, error: null, message: null });
    try {
      const items = await getCommunityHighlights({ limit: 6 });
      setCommunityPicks(items);
      setCommunityState({ loading: false, error: null, message: items.length ? null : EMPTY_SPONSORS_MESSAGE });
    } catch (err) {
      console.error('community highlights error', err);
      if (isApiError(err) && err.status === 404) {
        setCommunityPicks([]);
        setCommunityState({ loading: false, error: null, message: EMPTY_SPONSORS_MESSAGE });
      } else {
        setCommunityPicks([]);
        setCommunityState({ loading: false, error: FALLBACK_ERROR, message: null });
      }
    }
  }, []);

  const loadClassrooms = useCallback(async () => {
    setClassroomsState({ loading: true, error: null, message: null });
    try {
      const items = await getCommunityClassrooms({ limit: 6 });
      setClassrooms(items);
      setClassroomsState({ loading: false, error: null, message: items.length ? null : EMPTY_CLASSROOMS_MESSAGE });
    } catch (err) {
      console.error('community classrooms error', err);
      if (isApiError(err) && err.status === 404) {
        setClassrooms([]);
        setClassroomsState({ loading: false, error: null, message: EMPTY_CLASSROOMS_MESSAGE });
      } else {
        setClassrooms([]);
        setClassroomsState({ loading: false, error: FALLBACK_ERROR, message: null });
      }
    }
  }, []);

  const loadLockerRooms = useCallback(async () => {
    setLockerRoomsState({ loading: true, error: null, message: null });
    try {
      const items = await getCommunityLockerRooms({ limit: 6 });
      setLockerRooms(items);
      setLockerRoomsState({ loading: false, error: null, message: items.length ? null : 'No locker rooms available yet.' });
    } catch (err) {
      console.error('community locker rooms error', err);
      setLockerRooms([]);
      setLockerRoomsState({ loading: false, error: isApiError(err) && err.status === 404 ? null : FALLBACK_ERROR, message: null });
    }
  }, []);

  const loadClubPosts = useCallback(async () => {
    setClubPostsLoading(true);
    try {
      const res = await fetch(`${CORE_API}/api/v1/public/community-feed?limit=6`);
      if (res.ok) {
        const json = await res.json();
        setClubPosts(json.data?.posts || []);
      }
    } catch (e) {
      console.error('community club posts error', e);
    } finally {
      setClubPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeatured();
    loadCommunity();
    loadClassrooms();
    loadLockerRooms();
    loadClubPosts();
  }, [loadFeatured, loadCommunity, loadClassrooms, loadLockerRooms, loadClubPosts]);

  // Auto-rotation effect for community highlights
  useEffect(() => {
    const startRotation = () => {
      // Clear any existing timers
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);

      // Progress bar updates every 100ms
      progressTimerRef.current = setInterval(() => {
        setRotationProgress((prev) => {
          const next = prev + (100 / (ROTATION_INTERVAL_MS / 100));
          return next >= 100 ? 0 : next;
        });
      }, 100);

      // Rotate sponsors at the interval
      rotationTimerRef.current = setInterval(() => {
        if (!isRotationPaused) {
          setRotationProgress(0);
          loadCommunity();
        }
      }, ROTATION_INTERVAL_MS);
    };

    if (!isRotationPaused && communityPicks.length > 0) {
      startRotation();
    }

    return () => {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isRotationPaused, loadCommunity, communityPicks.length]);

  // Pause rotation when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsRotationPaused(document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const showSearchResults = searchQuery.trim().length > 0;

  return (
    <div className="space-y-12">
      <section className="rounded-3xl bg-gradient-to-r from-amber-100 via-rose-50 to-white p-10 text-center shadow-soft">
        <h1 className="text-4xl font-semibold text-cocoa">Discover our community.</h1>
        <p className="mt-4 text-lg text-cocoa/80">
          Explore the sponsors powering classroom experiences and the teachers sharing their journey on Project Tower.
        </p>
        <div className="mx-auto mt-8 flex justify-center">
          <CommunitySearch onResults={handleSearchResults} onStateChange={handleSearchState} />
        </div>
      </section>

      {showSearchResults ? (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-cocoa">Search results</h2>
            {searchState.loading ? <span className="text-sm text-cocoa/60">Searching…</span> : null}
          </div>
          {searchResults.length === 0 && !searchState.loading ? (
            <p className="text-sm text-cocoa/70">{searchState.notFound ? 'No results' : EMPTY_SPONSORS_MESSAGE}</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {searchResults.map((sponsor) => (
                <SponsorCard key={`${sponsor.user_id}-${sponsor.handle}`} sponsor={sponsor} />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Featured Classrooms Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-cocoa">Featured classrooms</h2>
            <p className="text-sm text-cocoa/60">Discover teachers sharing their classroom journey</p>
          </div>
          <button
            type="button"
            onClick={loadClassrooms}
            className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
          >
            Refresh
          </button>
        </div>
        {classroomsState.loading ? <p className="text-sm text-cocoa/60">Loading classrooms…</p> : null}
        {classroomsState.error ? <p className="text-sm text-brick">{classroomsState.error}</p> : null}
        {classrooms.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {classrooms.map((classroom) => (
              <ClassroomCard key={classroom.classroom_id} classroom={classroom} />
            ))}
          </div>
        ) : (
          !classroomsState.loading && !classroomsState.error && (
            <div className="rounded-2xl border border-dashed border-sand bg-shell/30 p-8 text-center">
              <p className="text-sm text-cocoa/70">{classroomsState.message || EMPTY_CLASSROOMS_MESSAGE}</p>
              <p className="mt-2 text-xs text-cocoa/50">
                Teachers can opt into the community rotation from their Public Classroom page.
              </p>
            </div>
          )
        )}
      </section>

      {/* Featured Locker Rooms Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-cocoa">Featured locker rooms</h2>
            <p className="text-sm text-cocoa/60">Athletic programs building champions on and off the field</p>
          </div>
          <button
            type="button"
            onClick={loadLockerRooms}
            className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
          >
            Refresh
          </button>
        </div>
        {lockerRoomsState.loading ? <p className="text-sm text-cocoa/60">Loading locker rooms...</p> : null}
        {lockerRoomsState.error ? <p className="text-sm text-brick">{lockerRoomsState.error}</p> : null}
        {lockerRooms.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {lockerRooms.map((lr) => (
              <LockerRoomCard key={lr.locker_room_id} lockerRoom={lr} />
            ))}
          </div>
        ) : (
          !lockerRoomsState.loading && !lockerRoomsState.error && (
            <div className="rounded-2xl border border-dashed border-sand bg-shell/30 p-8 text-center">
              <p className="text-sm text-cocoa/70">{lockerRoomsState.message || 'No locker rooms available yet.'}</p>
              <p className="mt-2 text-xs text-cocoa/50">
                Coaches can opt into the community rotation from their Public Page settings.
              </p>
            </div>
          )
        )}
      </section>

      {/* Platform Broadcasts */}
      <BroadcastFeed />

      {/* Shared Club Posts Section */}
      {(clubPosts.length > 0 || clubPostsLoading) && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-cocoa">From the clubs</h2>
              <p className="text-sm text-cocoa/60">Posts shared by our community members</p>
            </div>
            <button
              type="button"
              onClick={loadClubPosts}
              className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
            >
              Refresh
            </button>
          </div>
          {clubPostsLoading ? (
            <p className="text-sm text-cocoa/60">Loading club posts...</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {clubPosts.map((post) => (
                <CommunityPostCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Featured Sponsors Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-cocoa">Featured sponsors</h2>
          {featuredError ? <span className="text-sm text-brick">{featuredError}</span> : null}
          {!featuredError && featuredLoading ? (
            <span className="text-sm text-cocoa/60">Loading featured sponsors…</span>
          ) : null}
        </div>
        {featured.length === 0 && !featuredError && !featuredLoading ? (
          <p className="text-sm text-cocoa/70">{featuredMessage || EMPTY_SPONSORS_MESSAGE}</p>
        ) : (
          <div className="grid gap-4 overflow-x-auto sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featured.map((sponsor) => (
              <SponsorCard key={`${sponsor.user_id}-${sponsor.handle}`} sponsor={sponsor} />
            ))}
          </div>
        )}
      </section>

      <section
        className="space-y-4"
        onMouseEnter={() => setIsRotationPaused(true)}
        onMouseLeave={() => setIsRotationPaused(false)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-cocoa">From the community</h2>
            {communityPicks.length > 0 && !communityState.loading ? (
              <span className="text-xs text-cocoa/50" title="Cards rotate automatically">
                {isRotationPaused ? '⏸ Paused' : '↻ Auto-rotating'}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {isRotationPaused ? (
              <button
                type="button"
                onClick={() => setIsRotationPaused(false)}
                className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
                title="Resume auto-rotation"
              >
                Resume
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsRotationPaused(true)}
                className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
                title="Pause auto-rotation"
              >
                Pause
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setRotationProgress(0);
                loadCommunity();
              }}
              className="rounded-full border border-cocoa/30 px-3 py-1 text-sm font-semibold text-cocoa transition hover:border-cocoa"
            >
              Refresh
            </button>
          </div>
        </div>
        {communityPicks.length > 0 && !communityState.loading ? (
          <div className="h-1 w-full overflow-hidden rounded-full bg-sand/50">
            <div
              className="h-full rounded-full bg-cocoa/30 transition-all duration-100 ease-linear"
              style={{ width: `${isRotationPaused ? rotationProgress : rotationProgress}%` }}
            />
          </div>
        ) : null}
        {communityState.loading ? <p className="text-sm text-cocoa/60">Loading community highlights…</p> : null}
        {communityState.error ? <p className="text-sm text-brick">{communityState.error}</p> : null}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {communityPicks.map((sponsor) => (
            <SponsorCard key={`${sponsor.user_id}-${sponsor.handle}-community`} sponsor={sponsor} />
          ))}
        </div>
        {communityPicks.length === 0 && !communityState.loading && !communityState.error ? (
          <p className="text-sm text-cocoa/70">{communityState.message || EMPTY_SPONSORS_MESSAGE}</p>
        ) : null}
      </section>
    </div>
  );
}

// ── Broadcast Feed — recent platform broadcasts ──
function BroadcastFeed() {
  const [broadcasts, setBroadcasts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${CORE_API}/api/v1/social/broadcasts/live`)
      .then((r) => r.json())
      .then((json) => setBroadcasts(Array.isArray(json?.data) ? json.data.slice(0, 5) : []))
      .catch(() => setBroadcasts([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && broadcasts.length === 0) return null;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-cocoa">Platform Updates</h2>
        <p className="text-sm text-cocoa/60">Announcements and milestones from Project Tower</p>
      </div>
      {loading ? (
        <p className="text-sm text-cocoa/60">Loading updates...</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {broadcasts.map((b: any) => (
            <div
              key={b.id}
              className="rounded-xl border border-sand bg-white/80 p-4 shadow-soft space-y-2"
            >
              {b.media_url && (
                <img src={b.media_url} alt="" className="w-full rounded-lg object-cover max-h-40" />
              )}
              <p className="text-sm text-cocoa">{b.body}</p>
              {b.cta_label && b.cta_href && (
                <a
                  href={b.cta_href}
                  className="inline-block text-xs font-semibold text-brick hover:underline"
                  target={b.cta_href.startsWith('http') ? '_blank' : undefined}
                  rel={b.cta_href.startsWith('http') ? 'noreferrer' : undefined}
                >
                  {b.cta_label} &rarr;
                </a>
              )}
              <p className="text-[10px] text-cocoa/40">
                {new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
